import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Users, Building2, Kanban, CheckSquare, UserCog,
  LogOut, ChevronRight, SlidersHorizontal, Menu, KeyRound, Bell, Mail,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/deals", label: "Deals", icon: Kanban },
  { to: "/contacts", label: "Contacts", icon: Users },
  { to: "/companies", label: "Companies", icon: Building2 },
  { to: "/tasks", label: "Tasks", icon: CheckSquare },
];

function initials(name = "") {
  return name.split(" ").filter(Boolean).slice(0, 2).map((w) => w[0]?.toUpperCase()).join("") || "U";
}

/** Sidebar content — shared between the fixed desktop rail and the mobile sheet. */
function SidebarContent({ user, onNavigate }) {
  const linkClass = ({ isActive }) =>
    `flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors duration-150 ${
      isActive ? "bg-primary/8 text-primary font-medium" : "text-foreground hover:bg-accent"
    }`;
  const activeStyle = ({ isActive }) =>
    isActive ? { backgroundColor: "hsl(222 100% 33% / 0.08)" } : undefined;

  return (
    <>
      <div className="h-14 flex items-center px-4 border-b border-border">
        <img
          src="/brand/elevante-logo.png"
          alt="elevante.ai"
          className="h-7 w-auto select-none"
          data-testid="brand-logo"
          draggable={false}
        />
      </div>

      <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
        <div className="eyebrow px-2 pt-2 pb-1">Workspace</div>
        {NAV.map(({ to, label, icon: Icon, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            onClick={onNavigate}
            data-testid={`nav-${label.toLowerCase()}`}
            className={linkClass}
            style={activeStyle}
          >
            <Icon className="h-4 w-4" strokeWidth={1.75} />
            <span>{label}</span>
          </NavLink>
        ))}

        {user?.role === "admin" && (
          <>
            <div className="eyebrow px-2 pt-4 pb-1">Admin</div>
            <NavLink
              to="/team"
              onClick={onNavigate}
              data-testid="nav-team"
              className={linkClass}
              style={activeStyle}
            >
              <UserCog className="h-4 w-4" strokeWidth={1.75} />
              <span>Team</span>
            </NavLink>
            <NavLink
              to="/settings"
              onClick={onNavigate}
              data-testid="nav-settings"
              className={linkClass}
              style={activeStyle}
            >
              <SlidersHorizontal className="h-4 w-4" strokeWidth={1.75} />
              <span>Settings</span>
            </NavLink>
          </>
        )}
      </nav>

      <div className="p-3 border-t border-border">
        <div className="flex items-center gap-3 px-2 py-1.5">
          <Avatar className="h-8 w-8">
            <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
              {initials(user?.name)}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1">
            <div className="text-sm font-medium truncate">{user?.name}</div>
            <div className="text-xs text-muted-foreground truncate">
              {user?.role === "admin" ? "Admin" : "Sales Rep"}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-background">
      {/* Desktop sidebar — hidden on mobile */}
      <aside
        className="hidden md:flex w-64 shrink-0 border-r border-border bg-card flex-col"
        data-testid="sidebar"
      >
        <SidebarContent user={user} />
      </aside>

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-14 border-b border-border bg-card px-3 sm:px-6 flex items-center gap-2 sm:gap-4">
          {/* Mobile hamburger */}
          <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <SheetTrigger asChild>
              <button
                data-testid="mobile-nav-trigger"
                className="md:hidden -ml-1 h-9 w-9 inline-flex items-center justify-center rounded-md hover:bg-accent transition-colors"
                aria-label="Open navigation"
              >
                <Menu className="h-5 w-5" strokeWidth={1.75} />
              </button>
            </SheetTrigger>
            <SheetContent
              side="left"
              className="w-72 p-0 flex flex-col gap-0"
              data-testid="mobile-nav-sheet"
            >
              <VisuallyHidden>
                <SheetTitle>Navigation</SheetTitle>
              </VisuallyHidden>
              <SidebarContent user={user} onNavigate={() => setMobileNavOpen(false)} />
            </SheetContent>
          </Sheet>

          {/* Mobile-only compact brand */}
          <img
            src="/brand/elevante-logo.png"
            alt="elevante.ai"
            className="md:hidden h-6 w-auto select-none"
            data-testid="brand-logo-mobile"
            draggable={false}
          />

          <div className="ml-auto flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="user-menu-btn"
                  className="flex items-center gap-2 px-2 py-1.5 rounded-md hover:bg-accent transition-colors duration-150"
                >
                  <Avatar className="h-7 w-7">
                    <AvatarFallback className="bg-primary/10 text-primary text-[11px] font-semibold">
                      {initials(user?.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="text-sm hidden sm:inline">{user?.name}</span>
                  <ChevronRight className="h-4 w-4 text-muted-foreground rotate-90 hidden sm:inline" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-medium">{user?.name}</div>
                  <div className="text-xs text-muted-foreground">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="change-password-btn"
                  onClick={() => setChangePwOpen(true)}
                >
                  <KeyRound className="h-4 w-4 mr-2" /> Change password
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="notification-settings-btn"
                  onClick={() => setNotifOpen(true)}
                >
                  <Bell className="h-4 w-4 mr-2" /> Notification settings
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="logout-btn"
                  onClick={handleLogout}
                  className="text-destructive focus:text-destructive"
                >
                  <LogOut className="h-4 w-4 mr-2" /> Sign out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
      <Toaster position="top-right" richColors />
      <ChangePasswordDialog open={changePwOpen} onOpenChange={setChangePwOpen} />
      <NotificationSettingsDialog open={notifOpen} onOpenChange={setNotifOpen} />
    </div>
  );
}


/** Email notification preferences — pause/resume + fire a test digest. */
function NotificationSettingsDialog({ open, onOpenChange }) {
  const [prefs, setPrefs] = useState({ reminders_paused: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    api.get("/users/me/notification-prefs")
      .then(({ data }) => setPrefs({ reminders_paused: !!data.reminders_paused }))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [open]);

  const togglePause = async (val) => {
    setSaving(true);
    try {
      const { data } = await api.patch("/users/me/notification-prefs", { reminders_paused: val });
      setPrefs({ reminders_paused: !!data.reminders_paused });
      toast.success(val ? "Reminder emails paused" : "Reminder emails on");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTestDigest = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/notifications/test-digest");
      if (data.ok) toast.success("Test digest sent. Check your inbox.");
      else toast.error("Couldn't send test digest — check the server logs or SendGrid status.");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="notification-settings-dialog">
        <DialogHeader>
          <DialogTitle>Notification settings</DialogTitle>
          <DialogDescription>
            Control the emails Elevante sends you: 24-hour reminders on activities & tasks, and your end-of-day digest.
          </DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : (
          <div className="space-y-5 py-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="text-sm font-medium">Pause all reminder emails</div>
                <p className="text-xs text-muted-foreground mt-1 max-w-sm">
                  Stops 24-hour reminders and the daily digest for your account. You&rsquo;ll still receive account emails (password resets, etc.).
                </p>
              </div>
              <Switch
                checked={prefs.reminders_paused}
                onCheckedChange={togglePause}
                disabled={saving}
                data-testid="notif-pause-toggle"
              />
            </div>

            <div className="pt-4 border-t border-border space-y-2">
              <div className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <div className="text-sm font-medium">Send me a test digest now</div>
              </div>
              <p className="text-xs text-muted-foreground">
                Fires an immediate email with your current overdue + due-today + due-tomorrow items. Great for QA.
              </p>
              <Button
                variant="outline"
                size="sm"
                onClick={sendTestDigest}
                disabled={testing}
                data-testid="notif-test-digest"
              >
                {testing ? "Sending…" : "Send test digest"}
              </Button>
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="notif-close">Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/** Self-service password change. Requires current password + confirm-new. */
function ChangePasswordDialog({ open, onOpenChange }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [saving, setSaving] = useState(false);

  const reset = () => setForm({ current: "", next: "", confirm: "" });

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 6) {
      toast.error("New password must be at least 6 characters");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSaving(true);
    try {
      await api.post("/auth/change-password", {
        current_password: form.current,
        new_password: form.next,
      });
      toast.success("Password changed");
      reset();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => { if (!o) reset(); onOpenChange(o); }}
    >
      <DialogContent data-testid="change-password-dialog">
        <DialogHeader>
          <DialogTitle>Change your password</DialogTitle>
          <DialogDescription>
            Enter your current password, then set and confirm a new one. Other devices you&apos;re signed in on will be signed out.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label htmlFor="cp-current">Current password</Label>
            <Input
              id="cp-current"
              type="password"
              autoComplete="current-password"
              data-testid="cp-current"
              value={form.current}
              onChange={(e) => setForm({ ...form, current: e.target.value })}
              required
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-new">New password</Label>
            <Input
              id="cp-new"
              type="password"
              autoComplete="new-password"
              data-testid="cp-new"
              value={form.next}
              onChange={(e) => setForm({ ...form, next: e.target.value })}
              required
              minLength={6}
            />
            <p className="text-[11px] text-muted-foreground">At least 6 characters.</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cp-confirm">Confirm new password</Label>
            <Input
              id="cp-confirm"
              type="password"
              autoComplete="new-password"
              data-testid="cp-confirm"
              value={form.confirm}
              onChange={(e) => setForm({ ...form, confirm: e.target.value })}
              required
            />
            {form.confirm && form.next !== form.confirm && (
              <p className="text-[11px] text-destructive" data-testid="cp-mismatch">
                Passwords do not match.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="cp-cancel"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={saving || !form.current || !form.next || form.next !== form.confirm}
              data-testid="cp-submit"
              className="bg-primary text-primary-foreground hover:bg-primary/90"
            >
              {saving ? "Updating…" : "Update password"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
